/**
 * Storage keys used throughout the application.
 * This file serves as a central location for all storage key definitions
 * to ensure consistency and prevent typos.
 */

/** Core storage keys */
export const STORAGE_KEYS = {
  /** User settings object containing theme, premium status, features, etc. */
  SETTINGS: 'settings',
  /** Current theme (light/dark) */
  THEME: 'theme',
  /** Array of user's prompts */
  PROMPTS: 'prompts',
  /** Array of user's folders */
  FOLDERS: 'folders',
  /** User's subscription plan status */
  USER_PLAN: 'userPlan',
  /** Timestamp of last subscription check */
  LAST_SUBSCRIPTION_CHECK: 'lastSubscriptionCheck',
  /** Internal storage keys list */
  INTERNAL_KEYS: '_keys',
  /** Storage version */
  VERSION: 'version',
  /** User preference for color indicators */
  USE_COLOR_INDICATORS: 'useColorIndicators',
  /** User's sort preferences */
  SORT_PREFERENCES: 'sortPreferences',
  /** Sidebar collapsed state */
  SIDEBAR_COLLAPSED: 'sidebarCollapsed',
  /** Work filter state */
  WORK_FILTER: 'workFilter',
  /** Last selected folder */
  LAST_SELECTED_FOLDER: 'lastSelectedFolder',
  /** Telemetry enabled state */
  TELEMETRY_ENABLED: 'telemetryEnabled',
  /** Rate limiting for logs */
  LOG_RATE_LIMITS: 'logRateLimits',
  /** Deleted items tracking array */
  DELETED_IDS: 'deletedIds',
  /** Pending tombstone records for deleted items */
  PENDING_TOMBSTONES: 'pendingTombstones',
  /** Offline tombstone queue */
  OFFLINE_TOMBSTONES: 'offlineTombstones',
  /** First run experience shown state */
  FIRST_RUN_EXPERIENCE_SHOWN: 'firstRunExperienceShown',
  /** Web Store rating prompt state (usage counter + ask/deferred/answered status) */
  RATING_PROMPT_STATE: 'ratingPromptState',
  /** Per-item sync metadata (version/last_modified/tombstones + cursor) for the backend API sync client */
  API_SYNC_STATE: 'apiSyncState',
  /** Firebase uid/ID token/refresh token mirrored by the UI for the background's Stripe billing reconcile (StoredBillingAuth) */
  BILLING_AUTH: 'billingAuth',
} as const;

/** Constants for deletion tracking */
export const DELETION_CLEANUP_DAYS = 14; // 14-day retention period
export const DELETION_CLEANUP_MS = DELETION_CLEANUP_DAYS * 24 * 60 * 60 * 1000;

/** Authentication-related storage keys */
export const AUTH_STORAGE_KEYS = {
  /** Authentication token */
  AUTH_TOKEN: 'prompt-auth-token',
  /** Code verifier for PKCE authentication */
  AUTH_CODE_VERIFIER: 'prompt-auth-token-code-verifier',
} as const;

/** Feature flag storage key prefix */
export const FEATURE_KEY_PREFIX = 'feature_';

/** Type for core storage keys */
export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

/** Type for authentication storage keys */
export type AuthStorageKey = (typeof AUTH_STORAGE_KEYS)[keyof typeof AUTH_STORAGE_KEYS];

/** Helper function to create a feature flag storage key */
export function createFeatureKey(featureName: string): string {
  return `${FEATURE_KEY_PREFIX}${featureName}`;
}

/** Helper function to extract feature name from a feature flag storage key */
export function extractFeatureName(key: string): string | null {
  return key.startsWith(FEATURE_KEY_PREFIX) ? key.replace(FEATURE_KEY_PREFIX, '') : null;
}

/** Helper function to check if a key is a feature flag storage key */
export function isFeatureKey(key: string): boolean {
  return key.startsWith(FEATURE_KEY_PREFIX);
}
